/**
 * PanelCommand.js
 * Panel (dist_panel) + Feeder ekleme/silme komutları
 * Global undo/redo için scene ve dispatch referanslarını saklar
 *
 * Panel eklenirken:
 * 1. Component (dist_panel) oluşturulur
 * 2. Feeder (wire/cable) oluşturulur
 * 3. Three.js sahnesine panel ve wire eklenir
 */

import { addComponent, addFeeder, removeComponent, removeFeeder } from '../../../redux/simpleSupplySlice';
import { deleteWire } from '../../../redux/wireSlice';
import { setCommandType, setOperationData, setCommandMessage } from '../../../redux/operationSlice';
import { COMMANDS } from '../../../utils/commonProperties';
import * as THREE from 'three';
import { createPanel } from '../../../factory/symbols/createPanel';

class PanelCommand {
  /**
   * @param {string} action - 'ADD' veya 'DELETE'
   * @param {object} component - Panel component verisi
   * @param {object} feeder - Feeder verisi
   * @param {object} options - Ek seçenekler (scene, dispatch, wireId, restoreCallback, restoreVertices)
   */
  constructor(action, component, feeder, options = {}) {
    this.action = action; // 'ADD' veya 'DELETE'
    this.component = component;
    this.feeder = feeder;
    this.options = options;
    this.scene = options.scene; // 🟢 Sahne referansı
    this.dispatch = options.dispatch; // 🟢 Redux dispatch
    this.wireId = options.wireId; // 🟢 Wire ID (wireSlice'tan silmek için)

    // 🆕 Async REDO desteği için restore properties
    this.restoreVertices = options.restoreVertices || [];
    this.restoreSource = options.restoreSource || null;
    this.restoreCallback = options.restoreCallback || null;
    this.sceneData = options; // 🆕 sceneData olarak tüm options'ı sakla
  }

  /**
   * Panel + Feeder ekleme işlemini gerçekleştir
   */
  execute() {
    if (!this.scene || !this.dispatch) {
      return;
    }

    if (!this.component) {
      return;
    }


    // 1. Sahneye panel ekle
    const panelPosition = new THREE.Vector3(
      this.component.position.x,
      this.component.position.y,
      0
    );

    const panelInfo = {
      name: this.component.name || 'Panel',
      installedPower: this.component.metadata?.installedPower || 0,
      purposeOfUse: this.component.electrical?.purposeOfUse || 'Mesken',
      aboneTuru: this.component.electrical?.aboneTuru || 'Mesken',
    };

    const panelMesh = createPanel(
      this.scene,
      panelPosition,
      1,
      0,
      false,
      0,
      panelInfo
    );

    panelMesh.rotation.z = this.component.rotation?.z || 0;

    // Panel userData'sına id ve type ekle
    panelMesh.userData = {
      ...this.component,
      ...panelMesh.userData,
      type: 'electricalPanel',
      id: this.component.id,
      isElectrical: true
    };

    // ✅ Panel ile wire bağlantısı (sürükleme için)
    if (!panelMesh.userData.connectedWires) {
      panelMesh.userData.connectedWires = [];
    }
    panelMesh.userData.connectedWires.push(this.wireId);

    // ✅ Source panel'i bul (wire bağlantısı ve outs için)
    const sourcePanelId = this.options.sourcePanelId;
    let sourcePanel = null;
    if (sourcePanelId) {
      this.scene.traverse(obj => {
        if (obj.userData?.id === sourcePanelId) {
          sourcePanel = obj;
        }
      });
    }

    // ✅ Source panel ile wire bağlantısı (sürükleme için)
    if (sourcePanel && this.wireId) {
      if (!sourcePanel.userData.connectedWires) {
        sourcePanel.userData.connectedWires = [];
      }
      if (!sourcePanel.userData.connectedWires.includes(this.wireId)) {
        sourcePanel.userData.connectedWires.push(this.wireId);
      }
    }

    this.scene.add(panelMesh);

    // 2. Sahneye feeder wire ekle
    if (this.feeder?.vertices?.length > 1) {
      const geometry = new THREE.BufferGeometry().setFromPoints(
        this.feeder.vertices.map((v) => new THREE.Vector3(v.x, v.y, v.z))
      );
      const chosenColor = this.options?.color ?? this.component?.metadata?.color ?? 0x2233ff;
      const material = new THREE.LineBasicMaterial({ color: chosenColor });
      const feederLine = new THREE.Line(geometry, material);
      feederLine.name = 'electricalPanelWire';
      feederLine.userData = {
        id: this.wireId,
        type: 'polyline',
        layer: 'electricalPanel',
        isSelectable: true,
        componentId: this.component.id,
        panelID: sourcePanelId, // ✅ Kaynak panel ID (wire pozisyon güncellemesi için)
      };
      this.scene.add(feederLine);

      // ✅ Kaynak panel'in outs dizisine çıkış bilgisini ekle
      if (sourcePanel) {
        if (!sourcePanel.userData.outs) {
          sourcePanel.userData.outs = [];
        }
        sourcePanel.userData.outs.push({
          type: 'panel',
          lineId: this.wireId,
          wireId: this.wireId,
          panelId: this.component.id,
          panelName: this.component.name || 'Panel',
          vertices: this.feeder.vertices,
          wires: [{ id: this.wireId }]
        });
      }
    }

    // 3. Redux'a component ve feeder ekle
    this.dispatch(addComponent(this.component));
    this.dispatch(addFeeder(this.feeder));
  }

  /**
   * Panel + Feeder silme işlemini gerçekleştir (undo için)
   */
  undo() {
    if (!this.scene || !this.dispatch) {
      return;
    }

    // ✅ Source panel'in connectedWires'ından ve outs'tan wire'ı kaldır
    const sourcePanelId = this.options.sourcePanelId;
    if (sourcePanelId && this.wireId) {
      let sourcePanel = null;
      this.scene.traverse(obj => {
        if (obj.userData?.id === sourcePanelId) {
          sourcePanel = obj;
        }
      });
      if (sourcePanel) {
        // connectedWires'tan kaldır
        if (sourcePanel.userData.connectedWires) {
          const index = sourcePanel.userData.connectedWires.indexOf(this.wireId);
          if (index > -1) {
            sourcePanel.userData.connectedWires.splice(index, 1);
          }
        }
        // ✅ outs dizisinden de kaldır
        if (sourcePanel.userData.outs) {
          sourcePanel.userData.outs = sourcePanel.userData.outs.filter(out =>
            out.wireId !== this.wireId &&
            out.lineId !== this.wireId &&
            !(out.wires && out.wires[0]?.id === this.wireId)
          );
        }
      }
    }

    // 1. Sahnedeki panel ve wire'ı kaldır
    const toRemove = [];
    this.scene.traverse((obj) => {
      // Panel mesh'i bul (userData.id ile)
      if (obj.userData?.id === this.component.id) {
        toRemove.push(obj);
      }
      // Wire'ı bul (userData.componentId veya name ile)
      if (obj.name === 'electricalPanelWire' && obj.userData?.componentId === this.component.id) {
        toRemove.push(obj);
      }
      // Alternatif: wireId ile de bulabilir
      if (this.wireId && obj.userData?.id === this.wireId) {
        toRemove.push(obj);
      }
    });

    toRemove.forEach((obj) => {
      // Parent'tan sil
      if (obj.parent) {
        obj.parent.remove(obj);
      } else {
        this.scene.remove(obj);
      }

      // Geometry ve material temizliği
      if (obj.geometry) {
        obj.geometry.dispose();
      }
      if (obj.material) {
        if (Array.isArray(obj.material)) {
          obj.material.forEach((mat) => mat.dispose());
        } else {
          obj.material.dispose();
        }
      }

      // Group ise children'ları da temizle
      if (obj.children && obj.children.length > 0) {
        obj.traverse((child) => {
          if (child.geometry) child.geometry.dispose();
          if (child.material) {
            if (Array.isArray(child.material)) {
              child.material.forEach((mat) => mat.dispose());
            } else {
              child.material.dispose();
            }
          }
        });
      }
    });


    // 2. Redux'tan component, feeder ve wire'ı sil
    this.dispatch(removeComponent(this.component.id));
    this.dispatch(removeFeeder(this.feeder.id));

    // Wire slice'tan da sil (eğer wireId varsa)
    if (this.wireId) {
      this.dispatch(deleteWire(this.wireId));
    }

    // 3. 🔁 Yeniden çizim moduna geç (kullanıcı wire çizmeye devam edebilsin)
    if (this.options?.restoreCallback && typeof this.options.restoreCallback === 'function') {
      this.options.restoreCallback(this.options.restoreVertices || []);
    } else {
      console.warn('⚠️ No restoreCallback found or not a function');
    }
  }

}

export default PanelCommand;
